import { Constant } from "src/constant";

/**
 * Builds the otpauth key URI encoded in the MFA QR code, per the Key URI Format.
 *
 * The issuer appears twice by design - as the label prefix and as the `issuer` parameter - and the
 * format requires both to carry the same value; when they disagree an authenticator application is
 * free to display either, so the same enrolment shows up under different names depending on the
 * phone.
 *
 * Every interpolated value is percent-encoded, and each part of the label is encoded separately so
 * the colon separating issuer from account stays literal - that colon is structural, and encoding it
 * would leave the label unparseable. Encoding is what makes the alignment above safe: the issuer now
 * reaches the label, where a space or a separator would otherwise end the segment early. It also
 * protects the account, which the app's email validator allows to contain '%': unencoded, a client
 * decodes 'bob%40x@example.com' back to 'bob@x@example.com' and displays an address the user does
 * not have, while the code keeps working - so nothing surfaces the mistake.
 * @param secret The base32 TOTP shared secret.
 * @param account The caller's own identity, displayed under the issuer in the authenticator.
 * @returns The `otpauth://totp/...` URI to encode as a QR code.
 */
export function buildAuthenticatorUrl({ secret, account }: { secret: string; account: string }): string {
    const { issuer } = Constant.mfaAuthenticatorApplication;
    const label = `${encodeURIComponent(issuer)}:${encodeAccount(account)}`;
    return `otpauth://totp/${label}?secret=${encodeURIComponent(secret)}&issuer=${encodeURIComponent(issuer)}&digits=6`;
}

/**
 * Percent-encodes an account for the label while keeping its '@' literal. Every account here is an
 * email, so '@' is in 100% of enrolments while the characters encoding actually protects against are
 * rare - which makes restoring it the conservative choice rather than a cosmetic one: a client that
 * under-decodes would mangle the displayed address for every user. '@' is allowed unencoded in a
 * path segment, and the Key URI Format's own example writes it that way.
 * @param account The account to place in the label.
 * @returns The encoded account, '@' excepted.
 */
function encodeAccount(account: string): string {
    return encodeURIComponent(account).replace(/%40/g, '@');
}
